import { defineStore } from 'pinia';
import { ref,computed } from 'vue';
import AuthService, { isAuthEmbedded } from '../services/authService';
import msalConfig, {
  msalDomainHint,
  msalLoginScopes,
  preferredEmailDomain,
  preferredLoginHint,
} from './authConfig';
import { AccountInfo } from '@azure/msal-browser';

const authService = new AuthService(msalConfig);

/** Returned by login() so callers know whether navigation will leave the page. */
export type LoginResult = 'ok' | 'redirect' | 'failed';

export const useAuthStore = defineStore('authStore', () => {
    const accounts = ref<AccountInfo[] | null>(null);
    const isLoggedIn = ref<boolean>(false);

    const pickPreferredAccount = (all: AccountInfo[]): AccountInfo | undefined => {
      console.log("all.length ",all.length );
      if (all.length === 0) {
        return undefined;
      }
      if (preferredLoginHint) {
        const normalizedPreferredHint = preferredLoginHint.toLowerCase();
        const exact = all.find(
          (a) => a.username?.toLowerCase() === normalizedPreferredHint
        );
        if (exact) {
          return exact;
        }
      }
      const byDomain = all.find((a) =>
        a.username?.toLowerCase().endsWith(`@${preferredEmailDomain.toLowerCase()}`)
      );
      return byDomain ?? all[0];
    };
  
    const syncAccountsFromMsal = () => {
      const msal = authService.getMsalInstance();
      const all = msal.getAllAccounts();
      if (all.length > 0) {
        const preferred = pickPreferredAccount(all);
        msal.setActiveAccount(preferred ?? null);
        accounts.value = all;
        isLoggedIn.value = true;
      } else {
        accounts.value = null;
        isLoggedIn.value = false;
      }
    };

    const checkLoginStatus = async () => {
      // On page load/route guard, Pinia starts empty. Rehydrate from MSAL cache first.
      const cached = authService.getMsalInstance().getAllAccounts();
      if (cached.length > 0) {
        const preferred = pickPreferredAccount(cached);
        authService.getMsalInstance().setActiveAccount(preferred ?? null);
        accounts.value = cached;
        isLoggedIn.value = true;
        return;
      }

      const userAccounts = await authService.handleRedirect();
      if (userAccounts && userAccounts.length > 0) {
        const msal = authService.getMsalInstance();
        const preferred = pickPreferredAccount(userAccounts);
        msal.setActiveAccount(preferred ?? null);
        accounts.value = userAccounts;
        isLoggedIn.value = true;
      } else {
        accounts.value = null;
        isLoggedIn.value = false;
      }
    };

    /**
     * Top-level window: redirect to Azure AD.
     * Iframe (Canvas): popup login — redirect APIs are blocked by MSAL in embedded frames.
     */
    const login = async (): Promise<LoginResult> => {
      const msal = authService.getMsalInstance();
      const existing = msal.getAllAccounts();
      if (existing.length > 0) {
        const preferred = pickPreferredAccount(existing);
        msal.setActiveAccount(preferred ?? null);
        accounts.value = existing;
        isLoggedIn.value = true;
        return 'ok';
      }

      if (isAuthEmbedded()) {
        const loginHint = preferredLoginHint || msal.getActiveAccount()?.username || undefined;
        try {
          // Try no-UI account resolution first. This can skip account picker when
          // the hinted account already has an Azure session.
          await msal.loginPopup({
            scopes: [...msalLoginScopes],
            loginHint,
            domainHint: msalDomainHint,
            prompt: 'none',
          });
          syncAccountsFromMsal();
          return isLoggedIn.value ? 'ok' : 'failed';
        } catch {
          // Fallback to interactive popup when silent popup cannot complete.
        }

        try {
          await msal.loginPopup({
            scopes: [...msalLoginScopes],
            loginHint,
            domainHint: msalDomainHint,
          });
          syncAccountsFromMsal();
          return isLoggedIn.value ? 'ok' : 'failed';
        } catch (e) {
          console.error('Login popup failed (check popup blockers):', e);
          return 'failed';
        }
      }

      msal.loginRedirect({
        scopes: [...msalLoginScopes],
        domainHint: msalDomainHint,
      });
      return 'redirect';
    };
    
    const logout = async () => {
      const msal = authService.getMsalInstance();
      if (isAuthEmbedded()) {
        try {
          const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
          await msal.logoutPopup({
            account: account ?? undefined,
            postLogoutRedirectUri: msalConfig.auth.redirectUri as string,
          });
        } catch (e) {
          console.error('Logout popup failed:', e);
        }
      } else {
        msal.logoutRedirect({
          postLogoutRedirectUri: msalConfig.auth.redirectUri,
        });
      }
      isLoggedIn.value = false;
      accounts.value = null;
    };

    // Computed property to get the first user's info (email and name)
    const userEmail = computed(() => accounts.value?.[0]?.username || '');
    const userName = computed(() => accounts.value?.[0]?.name || 'User');


    return {
        accounts,
        isLoggedIn,
        checkLoginStatus,
        login,
        logout,
        userEmail,
        userName
    };
});